import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Mail, Calendar, Clock, CreditCard, CheckCircle, CalendarDays, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getServiceById } from '../data/pricing';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending Review', className: 'bg-amber-500' },
  approved: { label: 'Approved', className: 'bg-emerald-600' },
  rejected: { label: 'Rejected', className: 'bg-red-500' },
  paid: { label: 'Paid', className: 'bg-sky-600' },
  confirmed: { label: 'Confirmed', className: 'bg-emerald-600' },
  completed: { label: 'Completed', className: 'bg-sky-600' },
  cancelled: { label: 'Cancelled', className: 'bg-red-500' },
};

const TrackQuote = () => {
  const [email, setEmail] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/quotes/track/${encodeURIComponent(email)}`);
      setData(res.data);
      if (res.data.quotes.length === 0) {
        toast.info('No quotes found for this email');
      }
    } catch {
      toast.error('Failed to look up quotes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getBookingForQuote = (quoteId) => {
    return data?.bookings?.find(b => b.quoteId === quoteId);
  };

  return (
    <div>
      <SEO
        title="Track Your Quote"
        description="Check the status of your cleaning quote and booking. Enter your email to track."
        canonical="/track"
      />
      <section className="bg-gradient-to-br from-sky-50 to-emerald-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Track Your Quote</h1>
            <p className="text-base sm:text-lg text-slate-600">
              Enter your email address to view the status of your quotes and bookings
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Search Form */}
          <Card className="mb-8 border-sky-100" data-testid="track-form">
            <CardContent className="pt-6">
              <form onSubmit={handleTrack} className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="trackEmail" className="sr-only">Email</Label>
                  <Input
                    id="trackEmail"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="track-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="bg-sky-600 hover:bg-sky-700" data-testid="track-submit-btn">
                  <Search className="h-4 w-4 mr-2" />
                  {loading ? 'Searching...' : 'Track'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {searched && data && data.quotes.length === 0 && (
            <Card className="text-center" data-testid="no-results">
              <CardContent className="py-8">
                <Mail className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-4">No quotes found for this email address.</p>
                <Link to="/contact">
                  <Button className="bg-sky-600 hover:bg-sky-700">
                    Request a Quote <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {data && data.quotes.length > 0 && (
            <div className="space-y-4" data-testid="quote-results">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Your Quotes ({data.quotes.length})</h2>
              {data.quotes.map((quote) => {
                const booking = getBookingForQuote(quote.id);
                const svc = getServiceById(quote.serviceType);
                return (
                  <Card key={quote.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`quote-card-${quote.id}`}>
                    <CardContent className="py-5">
                      <div className="flex flex-col sm:flex-row justify-between gap-3 mb-3">
                        <div>
                          <p className="font-semibold text-slate-900 capitalize">
                            {svc?.name || quote.serviceType.replace('-', ' ')}
                          </p>
                          <p className="text-sm text-slate-500">
                            Submitted {new Date(quote.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(quote.status)}
                          {quote.quoteAmount && (
                            <Badge variant="outline">£{quote.quoteAmount.toFixed(2)}</Badge>
                          )}
                        </div>
                      </div>

                      {/* Status Info */}
                      {quote.status === 'pending' && (
                        <p className="text-sm text-amber-600 bg-amber-50 rounded-md p-3">
                          Your quote is being reviewed. We'll respond within 24-48 hours.
                        </p>
                      )}

                      {quote.status === 'approved' && !booking && (
                        <div className="bg-emerald-50 rounded-md p-3">
                          <p className="text-sm text-emerald-700 mb-2">
                            Your quote has been approved for £{quote.quoteAmount?.toFixed(2)}!
                          </p>
                          <Link to={`/booking?quoteId=${quote.id}`}>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="book-now-btn">
                              <CalendarDays className="h-4 w-4 mr-2" /> Book Now
                            </Button>
                          </Link>
                        </div>
                      )}

                      {booking && (
                        <div className="bg-sky-50 rounded-md p-3 mt-2">
                          <p className="text-sm font-semibold text-slate-800 mb-2">Booking Details</p>
                          <div className="grid sm:grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-sky-600" />
                              <span>{booking.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-sky-600" />
                              <span>{booking.timeSlot?.replace('-', ' - ')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-sky-600" />
                              <span>Deposit: {booking.depositPaid ? 'Paid' : 'Pending'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-sky-600" />
                              <span>Status: {booking.status}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrackQuote;
